import axios from "axios";

/**
 * Pick the API base URL.
 *
 * Production sites are served from a custom domain (e.g. msonlinetravel.com) and
 * Emergent's edge reverse-proxies `/api/*` from the same origin, so we use a
 * relative base — this dodges cross-origin DNS blocks and iOS Safari ITP issues
 * that previously caused hotel searches to silently return 0 results on some
 * mobile networks.
 *
 * Preview / localhost still use the env-provided backend URL because the
 * frontend and backend live on the same Emergent origin there.
 */
const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const isCustomDomain = typeof window !== "undefined"
    && window.location.hostname
    && !window.location.hostname.includes("emergentagent.com")
    && !window.location.hostname.includes("emergent.host")
    && !window.location.hostname.includes("localhost")
    && !window.location.hostname.startsWith("127.");

const BACKEND_URL = isCustomDomain ? "" : ENV_BACKEND;
export const API = `${BACKEND_URL}/api`;

/** Resolve image URL: absolute returns as-is, /api/uploads/... gets backend prefix */
export const resolveImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
    return url;
};

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
    // When the user is currently on a partner panel route, prefer the partner
    // JWT so admin-style endpoints (e.g. /admin/hotels/:id/rates) auto-scope to
    // the partner. Backend accepts the partner token on hotel-scoped admin
    // routes (see get_admin_or_hotel_owner). Falls back to master token.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/partner")) {
        try {
            const raw = localStorage.getItem("partner_session_v1");
            if (raw) {
                const pt = JSON.parse(raw).token;
                if (pt) {
                    config.headers.Authorization = `Bearer ${pt}`;
                    return config;
                }
            }
        } catch { /* ignore */ }
    }
    const token = localStorage.getItem("ms_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err.response?.status === 401) {
            // soft handle - components decide redirect
        }
        // ForcePasswordChange modal blocks the UI; keep page requests pending instead of throwing
        if (err.response?.status === 403 && err.response?.data?.detail === "PASSWORD_CHANGE_REQUIRED") {
            return new Promise(() => {});
        }
        return Promise.reject(err);
    }
);

export default api;

export const formatTRY = (n) =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);

export const formatDate = (dateStr) => {
    try {
        return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

export const formatShortDate = (dateStr) => {
    try {
        return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short" }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};
